import { Button } from "../Button/Button";
import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import "./Modal.less";
import IntlMessages from "../../../util/IntlMessages";
import { Icon_Close } from "../Icons/icons";
import { adjustModalPosition } from "./ModalScripts";

// Module-level escape stack — ensures only the topmost open modal closes on Escape
const escapeStack: Array<() => void> = [];

if (typeof window !== "undefined") {
   window.addEventListener(
      "keydown",
      (e: KeyboardEvent) => {
         if (e.key !== "Escape" || escapeStack.length === 0) return;
         e.preventDefault();
         e.stopPropagation();
         e.stopImmediatePropagation();
         escapeStack[escapeStack.length - 1]();
      },
      true,
   );
}

interface ModalProps {
   mainStyles?: React.CSSProperties;
   topBarStyles?: React.CSSProperties;
   onCancelButtonStyle?: React.CSSProperties;
   bodyStyles?: React.CSSProperties;
   footerStyles?: React.CSSProperties;
   children?: React.ReactNode;
   topBarContent?: React.ReactNode;
   footerContent?: React.ReactNode;
   onCancel?: (isOpen: boolean) => void;
   onOk?: (isOpen: boolean) => void;
   onOkText?: string;
   onOkDisabled?: boolean;
   customOnOkClassName?: string;
   backgroundShadow?: boolean;
   customShadowStyle?: React.CSSProperties;
   width?: number;
   height?: number;
   wrapClassName?: string;
   isOpen: boolean;
   customCloseIcon?: React.ReactNode;
   calculatePosition?: boolean;
}

/**
 * Modal component that displays content in a dialog window.
 *
 * @param {object} props - Properties for the modal component.
 * @param {React.CSSProperties} [props.mainStyles] - Main styles for the modal.
 * @param {React.CSSProperties} [props.topBarStyles] - Styles for the top bar of the modal.
 * @param {React.CSSProperties} [props.onCancelButtonStyle] - Styles for the cancel button.
 * @param {React.CSSProperties} [props.bodyStyles] - Styles for the body of the modal.
 * @param {React.CSSProperties} [props.footerStyles] - Styles for the footer of the modal.
 * @param {React.ReactNode} [props.children] - Children elements (content) inside the modal.
 * @param {React.ReactNode} [props.topBarContent] - Content of the top bar of the modal.
 * @param {React.ReactNode} [props.footerContent] - Content of the footer of the modal.
 * @param {(isOpen: boolean) => void} [props.onCancel] - Function to be called when the cancel button is clicked.
 * @param {(isOpen: boolean) => void} [props.onOk] - Function to be called when the ok button is clicked.
 * @param {string} [props.onOkText] - intl.messag text thats gonna show on confrim button at the bottom of the modal
 * @param {string} [props.customOnOkClassName] - custom class name for onOk button
 * @param {boolean} [props.backgroundShadow=true] - Whether to show a background shadow.
 * @param {React.CSSProperties} [props.customShadowStyle] - Custom styles for modal shadow
 * @param {number} [props.width] - Width of the modal (number).
 * @param {number} [props.height] - Height of the modal (number).
 * @param {string} [props.wrapClassName=""] - CSS class for the wrapper.
 * @param {boolean} props.isOpen - Whether the modal is open.
 * @param {React.ReactNode} [props.customCloseIcon] - Custom close icon.
 * @param {boolean} [props.calculatePosition=true] - Whether to calculate the position of the modal.
 *
 * @returns {React.ReactElement | null} Returns a JSX element or null.
 */
export const Modal: React.FC<ModalProps> = ({
   mainStyles,
   topBarStyles,
   onCancelButtonStyle,
   bodyStyles,
   footerStyles,
   children,
   topBarContent,
   footerContent,
   onCancel,
   onOk,
   onOkText = "OK",
   onOkDisabled = false,
   customOnOkClassName,
   backgroundShadow = true,
   customShadowStyle,
   width,
   height,
   wrapClassName = "",
   isOpen = false,
   customCloseIcon,
   calculatePosition = true,
}) => {
   const [targetElement, setTargetElement] = useState<HTMLDivElement>();

   const [dragging, setDragging] = useState<boolean>(false);

   //in those useStates, we are calculating initial position of the modal (middle of the screen)
   //if the modal is set to different width position is corrected accordingly
   const [posY, setPosY] = useState<number>(
      height ? window.innerHeight / 2 - height / 2 : window.innerHeight * 0.25,
   );
   const [posX, setPosX] = useState<number>(
      width ? window.innerWidth / 2 - width / 2 : window.innerWidth * 0.25,
   );
   const modalRef = useRef<HTMLDivElement>(null);

   const [startPosX, setStartPosX] = useState<number>(0);
   const [startPosY, setStartPosY] = useState<number>(0);

   const modalRoot = document.getElementById("modal-root");

   useLayoutEffect(() => {
      requestAnimationFrame(() => {
         if (modalRef.current) {
            const rect = modalRef.current.getBoundingClientRect();

            setPosX(window.innerWidth / 2 - rect.width / 2);
            setPosY(window.innerHeight / 2 - rect.height / 2);
         }
      });
   }, [isOpen, width, height]);

   // this function change modal position, when modal is changing its size
   const recalculatePosition = () => {
      if (!modalRef.current) return;
      const rect = modalRef.current.getBoundingClientRect();
      const adjustedX = window.innerWidth / 2 - rect.width / 2;
      const adjustedY = window.innerHeight / 2 - rect.height / 2;
      setPosX(adjustedX < 0 ? 0 : adjustedX);
      setPosY(adjustedY < 0 ? 0 : adjustedY);
   };

   useEffect(() => {
      // Resize observer to watch for size changes
      if (!isOpen) return;
      const resizeObserver = new ResizeObserver(() => {
         recalculatePosition();
      });
      const currentModal = modalRef.current;
      if (currentModal) {
         resizeObserver.observe(currentModal);
      }

      // Cleanup observer
      return () => {
         if (currentModal) {
            resizeObserver.unobserve(currentModal);
         }
         resizeObserver.disconnect();
      };
   }, [isOpen]);

   useEffect(() => {
      if (topBarContent && isOpen) {
         recalculatePosition();
      }
   }, [isOpen, topBarContent]);

   // this useEffect repositions modal when screen size changed.
   useEffect(() => {
      const handleResize = () => {
         setPosX(width ? window.innerWidth / 2 - width / 2 : window.innerWidth * 0.25);
         setPosY(height ? window.innerHeight / 2 - height / 2 : window.innerHeight * 0.25);
      };

      window.addEventListener("resize", handleResize);
      return () => {
         window.removeEventListener("resize", handleResize);
      };
   }, []);

   const handleMouseDown = (e: any) => {
      // we setDragging to true, to provide information, that the dragging is started
      // also we are setting default position of mouse
      setDragging(true);
      setTargetElement(e.target);
      setStartPosX(e.clientX);
      setStartPosY(e.clientY);
   };

   const handleMouseMove = (e: MouseEvent) => {
      if (dragging) {
         // Here we are calculating the difference in cursor position relative to the position of the modal window
         // in order to eliminate the modal losing effect. (ghost element dragging)
         const dx = e.clientX - startPosX;
         const dy = e.clientY - startPosY;

         // getting parent container (dragging functions are triggered in child component)
         const parentContainer = targetElement?.parentElement;

         // these variables are used to prevent the modal window from leaving the user's screen.
         const mainDivWidth = parentContainer?.offsetWidth;
         const mainDivHeight = parentContainer?.offsetHeight;

         const mainDivOffsetLeft = parentContainer?.offsetLeft;
         const mainDivOffsetTop = parentContainer?.offsetTop;

         const screenHeight = window.innerHeight;
         const screenWidth = window.innerWidth;

         if (
            mainDivHeight !== undefined &&
            mainDivWidth !== undefined &&
            mainDivOffsetLeft !== undefined &&
            mainDivOffsetTop !== undefined &&
            mainDivHeight !== undefined &&
            parentContainer
         ) {
            // here we calculate border positions of our modal
            const rightBorder = mainDivWidth + posX + dx;
            const bottomBorder = mainDivHeight + posY + dy;
            const topBorder = posY + dy;
            const leftBorder = posX + dx;

            //and condition those borders:
            setStartPosY(e.clientY);
            //when they meet the screen
            if (bottomBorder < screenHeight && topBorder > 0) {
               setPosY(posY + dy);
               //when they go off screen (bottom and top)
            } else if (bottomBorder >= screenHeight) {
               setPosY(screenHeight - mainDivHeight);
            } else if (topBorder <= 0) {
               setPosY(0);
            }

            setStartPosX(e.clientX);
            if (rightBorder < screenWidth && leftBorder > 0) {
               setPosX(posX + dx);
            } else if (rightBorder >= screenWidth) {
               setPosX(screenWidth - mainDivWidth);
            } else if (leftBorder <= 0) {
               setPosX(0);
            }
         }
      }
   };

   const handleMouseUp = () => {
      setDragging(false);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
   };
   const handleResize = () => {
      const { adjustedX, adjustedY } = adjustModalPosition(posX, posY, width, height);
      setPosX(adjustedX);
      setPosY(adjustedY);
   };

   useEffect(() => {
      window.addEventListener("resize", handleResize);
      return () => {
         window.removeEventListener("resize", handleResize);
      };
   }, [posX, posY, width, height]);

   useEffect(() => {
      if (dragging) {
         window.addEventListener("mousemove", handleMouseMove);
         window.addEventListener("mouseup", handleMouseUp);
      } else {
         window.removeEventListener("mousemove", handleMouseMove);
         window.removeEventListener("mouseup", handleMouseUp);
      }
   }, [dragging]);

   // modal position is setted by top and left style
   const styles: React.CSSProperties = calculatePosition
      ? {
           ...mainStyles,
           top: posY < 0 ? 0 : posY,
           left: posX < 0 ? 0 : posX,
           width: width,
           height: height,
        }
      : {
           ...mainStyles,
           width: width,
           height: height,
        };

   const handleOnCloseClick = (): void => {
      onCancel && onCancel(false);
   };

   const handleOnOkClick = (): void => {
      if (onOk && typeof onOk === "function") {
         onOk(true);
      }
   };

   // close modal by escape — pushes onto global stack so only topmost modal reacts
   useEffect(() => {
      if (!isOpen) return;

      const handler = () => onCancel?.(false);
      escapeStack.push(handler);

      return () => {
         const i = escapeStack.lastIndexOf(handler);
         if (i !== -1) escapeStack.splice(i, 1);
      };
   }, [isOpen, onCancel]);

   return (
      (isOpen && (
         <>
            {ReactDOM.createPortal(
               <>
                  {backgroundShadow && (
                     <div
                        style={customShadowStyle ? customShadowStyle : undefined}
                        className="modal-shadow"
                     />
                  )}
                  <div
                     onClick={(e) => e.stopPropagation()}
                     data-testid="main-modal-div"
                     className={wrapClassName + " modal-container"}
                     style={{ ...styles }}
                     ref={modalRef}
                  >
                     <div
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        role="drag-handle"
                        itemType="button"
                        data-testid="drag-handle"
                        className="modal-top-bar"
                        style={topBarStyles}
                     >
                        {topBarContent}
                        {onCancel && typeof onCancel === "function" && (
                           <Button
                              className="close-modal-button"
                              datatestid="close-modal"
                              htmlType="button"
                              style={onCancelButtonStyle}
                              onClick={handleOnCloseClick}
                           >
                              {customCloseIcon ? (
                                 customCloseIcon
                              ) : (
                                 <Icon_Close fill="none" stroke="#64748B" />
                              )}
                           </Button>
                        )}
                     </div>
                     <div className="modal-body" style={bodyStyles}>
                        {children}
                     </div>
                     <div style={footerStyles} className="modal-footer">
                        {footerContent}
                        {onOk && typeof onOk === "function" && (
                           <Button
                              className={
                                 customOnOkClassName ? customOnOkClassName : "onOk-modal-button"
                              }
                              onClick={handleOnOkClick}
                              buttonType="primary"
                              disabled={onOkDisabled}
                           >
                              <IntlMessages id={onOkText} />
                           </Button>
                        )}
                     </div>
                  </div>
               </>,
               //@ts-ignore
               modalRoot,
            )}
         </>
      )) ||
      null
   );
};

export default Modal;
